import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Moment from 'moment';
import IconButton from '@mui/material/IconButton';
import Input from '../theme/forms/Input';
import AddTurfToCrewDropZone from './AddTurfToCrewDropZone';
import { DoorCrewCardProvider } from './DoorCrewCardContext';
import SeatList from './SeatList';
import { DoorCrew, ShiftMeta, Canvasser, TurfCode, Turf } from './types';
import allowUber from '../uber/allowUber';
import { useUberRides } from '../uber/useUberRides';
import UberRideTable from '../uber/UberRideTable';
import UberSiteCrewModal from '../modals/UberSiteCrewModal';
import UberSubmitErrorModal from '../uber/UberSubmitErrorModal';
import UberRideInfoModal from '../uber/UberRideInfoModal';
import BaseModal from '../theme/modals/BaseModal';
import { updateDoorCrew, updateDoorCrewCanvasser } from '../../store/api/calls/doors';
import api from '../../store/api/api';

interface DoorCrewCardProps {
  crew: DoorCrew;
  shiftData: ShiftMeta[];
  canvassers: Canvasser[];
  turfCodes: TurfCode[];
  office: number;
  onDeleteCrew: () => void;
  onCanvasserUpdate: () => void;
  onDropoffRequest: () => void;
  onOpenTurfNoteModal: (turf: Turf & { crew_id: number }) => void;
}

function getShift(shiftData: ShiftMeta[], shiftId: number): ShiftMeta | null {
  return shiftData.find((d) => d.id == shiftId) || null;
}

function getShiftLabel(shift: ShiftMeta | null): string | null {
  if (!shift) return null;
  return (
    Moment(`${shift.shift_date} ${shift.start_time}`).format('LT') +
    ' - ' +
    Moment(`${shift.shift_date} ${shift.end_time}`).format('LT')
  );
}

function getEmptyUberRide(pickupDate: string | null) {
  return {
    scheduleType: 'ondemand',
    primaryRider: null,
    seats: [],
    pickupTime: '',
    pickupLocationType: '',
    pickupCustomAddress: '',
    dropoffLocationType: '',
    dropoffCustomAddress: '',
    stops: [],
    pickup_date: pickupDate,
  };
}

export default function DoorCrewCard({
  crew,
  shiftData,
  canvassers,
  turfCodes,
  office,
  onDeleteCrew,
  onCanvasserUpdate,
  onDropoffRequest,
  onOpenTurfNoteModal,
}: DoorCrewCardProps) {
  const shift = getShift(shiftData, crew.shift);
  const shiftLabel = getShiftLabel(shift);
  const borderColor = crew.color || '#62b03b';

  // crew.canvass is the doorcanvass id this crew belongs to (see DoorSchedule.jsx focusedDoorCanvass)
  const campaignId = useSelector((state: any) => state.session.focusedCampaign?.id);
  const [isUberAllowed, setIsUberAllowed] = useState(false);
  // car icon swaps the card body between the crew-assembly view (seats/turfs) and
  // this transport panel -- mirrors DraggableSiteRouteCrew's toggleTransportPanel
  const [showTransportConfig, setShowTransportConfig] = useState(false);
  const [transportType, setTransportType] = useState<'driver' | 'uber'>(crew.transport_type || 'driver');
  const [showUberModal, setShowUberModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [uberRide, setUberRide] = useState<any>(() => getEmptyUberRide(shift?.shift_date || null));
  const [savingRide, setSavingRide] = useState(false);

  const requestDeleteCrew = () => setShowDeleteConfirm(true);
  const confirmDeleteCrew = () => {
    setShowDeleteConfirm(false);
    onDeleteCrew();
  };

  const {
    uberRides,
    loadingAllRides,
    loadingRideIds,
    cancelRide,
    submitRide,
    reloadRides,
    submitError,
    clearSubmitError,
    infoModal,
    fetchRideInfo,
    closeRideInfo,
  } = useUberRides(crew.id);

  useEffect(() => {
    let cancelled = false;
    allowUber({ campaign_id: campaignId, sitecanvass_id: null, doorcanvass_id: crew.canvass }).then((allowed: boolean) => {
      if (!cancelled) setIsUberAllowed(allowed);
    });
    return () => {
      cancelled = true;
    };
  }, [campaignId, crew.canvass]);

  useEffect(() => {
    setTransportType(crew.transport_type || 'driver');
  }, [crew.transport_type]);

  const handleTransportTypeChange = async (val: 'driver' | 'uber') => {
    const prevType = transportType;
    setTransportType(val);

    try {
      await updateDoorCrew(crew.id, { transport_type: val });

      // dropoff/ending-pickup points assigned while on Driver are plain geocoded
      // addresses -- not necessarily valid Uber pickup/dropoff locations (see
      // TurfDropoffMapModalUber's request-zone refinement) -- so switching to Uber
      // clears them, forcing a re-assignment through the Uber-aware dropoff map
      // rather than silently reusing a possibly-incompatible point.
      if (val === 'uber' && prevType === 'driver') {
        const clearedFields = {
          dropoff_lat: null,
          dropoff_lon: null,
          dropoff_address: null,
          ending_pickup_lat: null,
          ending_pickup_lon: null,
          ending_pickup_address: null,
        };
        await Promise.all(
          crew.seats
            .filter((seat) => seat.dropoff_lat != null || seat.ending_pickup_lat != null)
            .map((seat) => updateDoorCrewCanvasser(seat.id, clearedFields))
        );
      }
    } catch (err) {
      console.error('Failed to update crew transport type', err);
      alert('Failed to save transport type');
      setTransportType(prevType);
    }
  };

  const toggleTransportPanel = () => {
    const willBeShown = !showTransportConfig;
    setShowTransportConfig(willBeShown);
    if (willBeShown) reloadRides();
  };

  const handleOpenNoteModal = (turf: Turf, crewId: number) => {
    onOpenTurfNoteModal({ ...turf, crew_id: crewId });
  };

  // door crews don't have a backend ride-creation path yet -- /uber/ride-requests/
  // only knows how to attach a SiteCrewRequest (crew_data is strictly typed to
  // SiteCrewRequestSerializer in uber/serializers.py, referencing main.Crew /
  // main.RouteSeat), and there's no DoorCrewRequest equivalent. We still send the
  // request -- RideRequest.requesttypecode already has a DOORCREW="D" choice
  // reserved for this -- but until that serializer support exists on the backend
  // this will 400/500. Frontend-only, per request.
  const handleSaveUberRide = async () => {
    const decode = (str: string) => {
      if (!str) return null;
      try {
        return JSON.parse(str);
      } catch {
        return null;
      }
    };

    const getGeocode = (addressDataObject: any) => {
      if (!addressDataObject) return { latitude: null, longitude: null };
      return {
        latitude: addressDataObject.latitude || null,
        longitude: addressDataObject.longitude || null,
      };
    };

    const pickupObj = decode(uberRide.pickupLocationType);
    const dropoffObj = decode(uberRide.dropoffLocationType);

    const pickupGeocode = getGeocode(uberRide.pickupAddressData);
    const dropoffGeocode = getGeocode(uberRide.dropoffAddressData);

    if (pickupObj.type === 'other_address') {
      pickupObj.type = 'address';
    }

    const pickup_payload = pickupObj
      ? {
          type: pickupObj.type,
          office_id: pickupObj.type === 'office' ? office : null,
          site_id: pickupObj.type === 'site' ? pickupObj.site_id : null,
          // other_address (a seat's own saved dropoff/pickup, remapped above) carries
          // its address on `pickupObj.address`; manually-typed addresses have no such
          // field and fall back to the typed value in uberRide.pickupCustomAddress
          custom_address: pickupObj.type === 'address' ? pickupObj.address || uberRide.pickupCustomAddress || '' : null,
          latitude: pickupGeocode.latitude || pickupObj.latitude || null,
          longitude: pickupGeocode.longitude || pickupObj.longitude || null,
          access_point_id: pickupObj.type === 'address' && uberRide.pickupAccessPoint ? uberRide.pickupAccessPoint.id : null,
          secondary_display_name: pickupObj.type === 'address' && uberRide.pickupAccessPoint ? uberRide.pickupAccessPoint.label : null,
          secondary_latitude: pickupObj.type === 'address' && uberRide.pickupAccessPoint ? uberRide.pickupAccessPoint.latitude : null,
          secondary_longitude: pickupObj.type === 'address' && uberRide.pickupAccessPoint ? uberRide.pickupAccessPoint.longitude : null,
          access_point_wayfinding_note: pickupObj.type === 'address' && uberRide.pickupAccessPoint ? uberRide.pickupAccessPoint.rider_wayfinding_note : null,
        }
      : null;

    if (dropoffObj.type === 'other_address') {
      dropoffObj.type = 'address';
    }

    const dropoff_payload = dropoffObj
      ? {
          type: dropoffObj.type,
          office_id: dropoffObj.type === 'office' ? office : null,
          site_id: dropoffObj.type === 'site' ? dropoffObj.site_id : null,
          custom_address: dropoffObj.type === 'address' ? dropoffObj.address || '' : null,
          latitude: dropoffGeocode.latitude || dropoffObj.latitude || null,
          longitude: dropoffGeocode.longitude || dropoffObj.longitude || null,
          access_point_id: dropoffObj.type === 'address' && uberRide.dropoffAccessPoint ? uberRide.dropoffAccessPoint.id : null,
          access_point_label: dropoffObj.type === 'address' && uberRide.dropoffAccessPoint ? uberRide.dropoffAccessPoint.label : null,
          access_point_latitude: dropoffObj.type === 'address' && uberRide.dropoffAccessPoint ? uberRide.dropoffAccessPoint.latitude : null,
          access_point_longitude: dropoffObj.type === 'address' && uberRide.dropoffAccessPoint ? uberRide.dropoffAccessPoint.longitude : null,
          access_point_wayfinding_note: dropoffObj.type === 'address' && uberRide.dropoffAccessPoint ? uberRide.dropoffAccessPoint.rider_wayfinding_note : null,
        }
      : null;

    const stops = (uberRide.stops || []).map((stop: any, index: number) => {
      const obj = decode(stop.locationType) || {};
      // a seat's saved address arrives as type 'other_address' with the address and
      // coordinates embedded in the decoded object (same remap as pickup/dropoff)
      if (obj.type === 'other_address') {
        obj.type = 'address';
      }
      const stopGeocode = getGeocode(stop.customAddressData);

      return {
        ordinal_position: index + 1,
        uberlocation: {
          type: obj.type || null,
          site_id: obj.type === 'site' ? obj.site_id : null,
          office_id: obj.type === 'office' ? office : null,
          custom_address: obj.type === 'address' ? stop.customAddress || obj.address || '' : null,
          latitude: obj.type === 'address' ? stopGeocode.latitude || obj.latitude || null : null,
          longitude: obj.type === 'address' ? stopGeocode.longitude || obj.longitude || null : null,
          access_point_id: obj.type === 'address' && stop.accessPoint ? stop.accessPoint.id : null,
          access_point_label: obj.type === 'address' && stop.accessPoint ? stop.accessPoint.label : null,
          access_point_latitude: obj.type === 'address' && stop.accessPoint ? stop.accessPoint.latitude : null,
          access_point_longitude: obj.type === 'address' && stop.accessPoint ? stop.accessPoint.longitude : null,
          access_point_wayfinding_note: obj.type === 'address' && stop.accessPoint ? stop.accessPoint.rider_wayfinding_note : null,
        },
      };
    });

    const riders_payload = (uberRide.seats || []).map((seatNum: number) => ({
      seat_number: seatNum,
      is_primary_rider: seatNum === uberRide.primaryRider,
      canvasser_id: crew.seats[seatNum - 1]?.canvasser ?? null,
    }));

    if ((uberRide.seats || []).length) {
      const primaryCount = riders_payload.filter((r: any) => r.is_primary_rider).length;
      if (primaryCount !== 1) {
        alert('Please select exactly one primary rider seat.');
        return;
      }
    }

    const crewSeats = (uberRide.seats || []).map((seatNum: number) => {
      const seat = crew.seats[seatNum - 1];
      if (!seat) {
        throw new Error(`No seat found for UI seat ${seatNum}. Crew has ${crew.seats.length} seats.`);
      }
      return {
        seat_number: seatNum,
        crewcanvasser_id: seat.crewcanvasser_id,
      };
    });

    const crew_data = {
      doorcanvass: crew.canvass,
      crew: crew.id,
      seats: crewSeats,
    };

    const now = new Date();
    const todayLocalDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
      now.getDate()
    ).padStart(2, '0')}`;

    const payload = {
      requesttypecode: 'D',

      crew_id: crew.id,
      doorcanvass_id: crew.canvass,
      shift_id: crew.shift,
      campaign_id: campaignId || 1,

      schedule_type: uberRide.scheduleType,
      pickup_time: uberRide.scheduleType === 'scheduled' ? uberRide.pickupTime : null,
      pickup_date: shift?.shift_date || uberRide.pickup_date || todayLocalDate,

      primary_rider: uberRide.primaryRider,
      seats: uberRide.seats,
      riders: riders_payload,

      pickup: pickup_payload,
      dropoff: dropoff_payload,
      stops: stops,

      crew_data: crew_data,
      is_flexible_pickup: uberRide.scheduleType === 'flexible',
      is_ondemand_ride: uberRide.scheduleType === 'ondemand',
      is_scheduled_ride: uberRide.scheduleType === 'scheduled',

      status: 'approved',
    };

    setSavingRide(true);
    try {
      const response = await api.post('/uber/ride-requests/', payload);
      console.log('Door crew ride saved successfully', response.data);

      await reloadRides();

      setShowUberModal(false);
      setUberRide(getEmptyUberRide(shift?.shift_date || null));
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.non_field_errors?.[0] ?? 'An error occurred');
    } finally {
      setSavingRide(false);
    }
  };

  const useUber = transportType === 'uber';

  const transportOptions = [{ display: 'Driver', value: 'driver' }];
  if (isUberAllowed) {
    transportOptions.push({ display: 'Uber', value: 'uber' });
  }

  // adapts DoorCrew.seats to the shape UberSiteCrewModal expects from site crews'
  // routeseats -- door crews have no real "sites", but each seat's own assigned
  // dropoff/ending-pickup points (address + lat/lon, set via the turf dropoff map)
  // are a natural stand-in, so the pickup/dropoff/stop dropdowns can offer them
  // directly instead of always falling back to "(No sites assigned)". `kind`/`label`
  // are read by UberSiteCrewModal.buildGroupedOptions() purely to scope cross-seat
  // grouping (a seat's dropoff never merges with another seat's pickup, even at
  // identical coordinates) and to label each option -- they aren't persisted;
  // whichever one gets selected is saved as a plain address regardless of source.
  const pseudoRouteseats = crew.seats.map((seat) => (
  {
    canvasser_id: seat.canvasser,
    other_seat_address: [
      ...(seat.dropoff_lat != null && seat.dropoff_lon != null
        ? [
            {
              kind: 'dropoff',
              label: 'Dropoff',
              address_display: seat.dropoff_address || 'Dropoff',
              addressline1: '',
              latitude: seat.dropoff_lat,
              longitude: seat.dropoff_lon,
            },
          ]
        : []),
      ...(seat.ending_pickup_lat != null && seat.ending_pickup_lon != null
        ? [
            {
              kind: 'ending_pickup',
              label: 'Ending Pickup',
              address_display: seat.ending_pickup_address || 'Ending Pickup',
              addressline1: '',
              latitude: seat.ending_pickup_lat,
              longitude: seat.ending_pickup_lon,
            },
          ]
        : []),
    ],
  }));

  return (
    <div className="assign-crew-card door-crew-card" data-component="DoorCrewCard">
      <div
        className="crew-card-header"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
        data-component="DoorCrewCard"
        data-attr="door-crew-header"
      >
        <div className="crew-title" style={{ color: borderColor }}>
          Crew {crew.id}
          {shiftLabel ? ` · ${shiftLabel}` : null}
        </div>

        <div className="crew-card-top-icons-right" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <i
            className="fas fa-car"
            onClick={toggleTransportPanel}
            title="Crew transport"
            style={{ cursor: 'pointer' }}
            data-component="DoorCrewCard"
            data-attr="door-crew-transport-toggle"
          />

          <IconButton
            size="small"
            onClick={requestDeleteCrew}
            title="Delete crew"
            className="remove-crew-icon"
            data-component="DoorCrewCard"
            data-attr="door-crew-delete"
          >
            <i className="far fa-times-circle" />
          </IconButton>
        </div>
      </div>

      {showTransportConfig ? (
        <div
          className="transport-panel"
          style={{
            padding: '12px',
            background: '#fafafa',
            border: '1px solid #ddd',
            borderRadius: '4px',
            marginTop: '10px',
          }}
        >
          <label className="meta-label">Transport Type</label>

          <div style={{ maxWidth: '200px', marginBottom: '10px' }}>
            <Input
              type="select"
              value={transportType}
              options={transportOptions}
              onChange={handleTransportTypeChange}
            />
          </div>

          {useUber && isUberAllowed && (
            <div className="uber-rides-section" style={{ position: 'relative' }}>
              <h4>Uber Rides</h4>

              {loadingAllRides && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                  }}
                >
                  <i className="fa fa-spinner fa-spin" style={{ fontSize: '32px', color: '#666' }} />
                </div>
              )}

              <UberRideTable
                rides={uberRides}
                loadingRideIds={loadingRideIds}
                onCancelRide={cancelRide}
                onSubmitToUber={submitRide}
                infoModal={infoModal}
                onFetchRideInfo={fetchRideInfo}
                onCloseRideInfo={closeRideInfo}
              />

              <button
                className="button"
                onClick={() => {
                  setUberRide(getEmptyUberRide(shift?.shift_date || null));
                  setShowUberModal(true);
                }}
              >
                + New Uber Ride
              </button>
            </div>
          )}
        </div>
      ) : (
        <DoorCrewCardProvider
          crew={crew}
          canvassers={canvassers}
          turfCodes={turfCodes}
          onCanvasserUpdate={onCanvasserUpdate}
          onDeleteCrew={onDeleteCrew}
          onOpenNoteModal={handleOpenNoteModal}
        >
          <SeatList color={borderColor} />
          <AddTurfToCrewDropZone crewId={crew.id} />
        </DoorCrewCardProvider>
      )}

      {!showTransportConfig && (
        <div className="crew-card-bottom-buttons">
          <div className="button button-clear" onClick={onDropoffRequest}>
            Assign Locations
          </div>
        </div>
      )}

      <UberSiteCrewModal
        show={showUberModal}
        uberRide={uberRide}
        routeseats={pseudoRouteseats}
        canvassers={canvassers}
        office={office}
        seatCount={crew.seats.length}
        saving={savingRide}
        onUpdate={(changes: any) => setUberRide((prev: any) => ({ ...prev, ...changes }))}
        onClose={() => setShowUberModal(false)}
        onSave={handleSaveUberRide}
        onCancel={() => setShowUberModal(false)}
      />

      <UberSubmitErrorModal show={!!submitError} errorMessage={submitError} onClose={clearSubmitError} />
      <UberRideInfoModal
        show={infoModal?.show}
        rideInfo={infoModal?.data}
        loading={infoModal?.loading}
        onClose={closeRideInfo}
      />

      <BaseModal open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} center>
        <div
          className="modal-internal-wrapper"
          data-component="DoorCrewCard"
          data-attr="door-crew-delete-confirm-modal"
        >
          <p>
            Are you sure you want to delete <b>Crew {crew.id}</b>?
          </p>
          <div className="button-wrapper">
            <div
              className="button"
              onClick={confirmDeleteCrew}
              data-component="DoorCrewCard"
              data-attr="door-crew-delete-confirm"
            >
              Delete
            </div>
            <div
              className="button button-outline"
              onClick={() => setShowDeleteConfirm(false)}
              data-component="DoorCrewCard"
              data-attr="door-crew-delete-cancel"
            >
              Cancel
            </div>
          </div>
        </div>
      </BaseModal>
    </div>
  );
}
